import "react-day-picker/style.css";

import * as RadioGroup from "@radix-ui/react-radio-group";
import { Separator } from "@radix-ui/react-select";
import clsx from "clsx";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import { enUS, ru } from "react-day-picker/locale";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { formatTime } from "~/common/formatters";
import { Calendar } from "~/common/redesign-v1";
import { Loader } from "~/components/ui/loader";
import { useSearchParams } from "~/hooks/use-search-params";
import type { DateTimeStringUTC } from "~/types/common";

import { DateTimeHeader } from "./book-form";
import { formatDate, formatDateToYYYYMMDD, getTimeZone } from "./calendar-utils";
import { dayPickerClassNames } from "./day-picker-styles";
import type { TMeetingSlot } from "./meetings-types";
import { useIsRedesignMeetings } from "./redesign-meetings-context";

export type CalendarWidgetProps = React.ComponentProps<typeof CalendarWidget>;

/**
 * Builds a slot link that SETS `time` while preserving every other search param.
 *
 * A bare `to="?time=…"` replaces the whole query string. That's invisible on the booking page (no
 * other params), but this same calendar renders inside the reschedule dialog on the consultations
 * page, whose active tab lives in `?tab=…` — dropping it snapped the page back to the default tab
 * and tore the dialog down mid-pick. Preserving params is correct in both hosts.
 */
function useSlotLink() {
  const [searchParams] = useSearchParams();

  return (time: DateTimeStringUTC) => {
    const next = new URLSearchParams(searchParams);
    next.set("time", time);
    // `URLSearchParams.toString()` percent-encodes the timestamp's ":" (…T15%3A00%3A00Z). That still
    // reads back fine, but ":" is legal in a query (RFC 3986) and the readable form is what every
    // existing link, bookmark and test uses — so keep the URL shape unchanged.
    return `?${next.toString().replace(/%3A/g, ":")}`;
  };
}

export function CalendarWidget({ slots, slotsAreLoading, onMonthChange }: { slots: TMeetingSlot[]; slotsAreLoading: boolean; onMonthChange(date: Date): void }) {
  const intl = useIntl();
  const redesign = useIsRedesignMeetings();

  const [selectedDate, setSelectedDate] = useState(() => findNearestSlotDate(slots));
  const [selectedTime, setSelectedTime] = useState<DateTimeStringUTC>("");

  // Sets the first available selected date when we load slots for the month
  useEffect(() => {
    setSelectedDate(findNearestSlotDate(slots));
  }, [slots, slotsAreLoading]);

  const selectedDateStr = selectedDate ? formatDateToYYYYMMDD(selectedDate) : "";
  const slot = slots.find((slot) => slot.date == selectedDateStr);

  // Filter dates that have at least one available time slot (not past and at least 1 hour from now)
  const now = new Date();
  const minBookingTime = new Date(now.getTime() + 60 * 60 * 1000);
  const datesWithSlots = new Set(slots.filter((slot) => slot.times.some(({ time }) => new Date(time) > minBookingTime)).map((slot) => slot.date));

  return (
    <>
      {selectedTime ? <DateTimeHeader time={selectedTime} /> : null}

      <div className={clsx("tw-flex max-sm:tw-flex-col", redesign ? "tw-w-full tw-border-b tw-border-border" : "tw-justify-center tw-border-b tw-border-[#f1f1f1]")}>
        {redesign ? (
          // NOT `navLayout="around"`: `Calendar`'s `nav` classNames assume the DEFAULT layout (a
          // single shared `<nav>` wrapping both buttons, flexed with `justify-between`) — "around"
          // renders `button_previous`/`button_next` as separate siblings of the caption instead, which
          // this skin doesn't style for. See `common/redesign-v1/ui/calendar.tsx`'s doc comment.
          // The booking card supplies the surface; retain padding but remove the calendar's own card chrome.
          <Calendar
            required
            className="tw-w-fit tw-border-0 tw-bg-transparent tw-p-4 tw-shadow-none"
            locale={{ ru, en: enUS }[intl.locale]}
            mode="single"
            disabled={(date) => isPast(date) || !datesWithSlots.has(formatDateToYYYYMMDD(date))}
            selected={selectedDate}
            onSelect={setSelectedDate}
            onPrevClick={onMonthChange}
            onNextClick={onMonthChange}
          />
        ) : (
          <DayPicker
            required
            locale={{ ru, en: enUS }[intl.locale]}
            mode="single"
            navLayout="around"
            disabled={(date) => isPast(date) || !datesWithSlots.has(formatDateToYYYYMMDD(date))}
            classNames={dayPickerClassNames}
            selected={selectedDate}
            onSelect={setSelectedDate}
            onPrevClick={onMonthChange}
            onNextClick={onMonthChange}
          />
        )}

        <DesktopSlots selectedDate={selectedDate} slot={slot} isLoading={slotsAreLoading} selectedTime={selectedTime} onTimeSelect={setSelectedTime} />

        <MobileSlots selectedTime={selectedTime} slot={slot} isLoading={slotsAreLoading} onSelectedTimeChange={setSelectedTime} />
      </div>

      <TimeZoneDisplay className="tw-mb-2 max-sm:tw-hidden" />
    </>
  );
}

function DesktopSlots({
  selectedDate,
  slot,
  isLoading,
  selectedTime,
  onTimeSelect,
}: {
  selectedDate: Date | undefined;
  slot?: TMeetingSlot;
  isLoading: boolean;
  selectedTime: DateTimeStringUTC;
  onTimeSelect: (time: DateTimeStringUTC) => void;
}) {
  const slotLink = useSlotLink();
  const redesign = useIsRedesignMeetings();
  const isEmpty = slot == undefined && !isLoading;
  const now = new Date();
  // Minimum time is 1 hour from now (for clinical expert bookings)
  const minBookingTime = new Date(now.getTime() + 60 * 60 * 1000);

  return (
    <div
      className={clsx(
        "tw-relative tw-flex tw-flex-col tw-border-l max-sm:tw-hidden",
        // The redesign calendar sizes to its grid, so the slot column grows to fill the remaining row.
        redesign ? "tw-min-w-[250px] tw-flex-1 tw-border-border" : "tw-w-[250px] tw-border-[#f1f1f1]",
        { "tw-items-center tw-justify-center": isLoading },
      )}
    >
      {isLoading ? (
        <Loader />
      ) : isEmpty ? (
        <p className={clsx("tw-m-0 tw-p-5 tw-text-xs", redesign ? "tw-font-sans tw-text-muted-foreground" : "tw-text-[#adadad]")}>
          <FormattedMessage id="meetings.no_time_available" />
        </p>
      ) : selectedDate && slot && hasAvailableSlots(slot) ? (
        <>
          <div className={clsx("tw-border-b tw-py-5 tw-pl-5", redesign ? "tw-border-border tw-font-sans tw-text-foreground" : "tw-border-[#f1f1f1] tw-text-[#0b2d2d]")}>{formatDate(selectedDate)}</div>

          <ul
            role="list"
            className={clsx(
              "tw-flex tw-list-none tw-flex-col tw-gap-2.5 tw-overflow-auto",
              "tw-absolute tw-inset-0 tw-top-[62px] tw-m-0 tw-px-5 tw-py-4",
              "[&::-webkit-scrollbar]:tw-w-1.5",
              // Booking and reschedule share this list. Apply neutral redesign scrollbar tokens in WebKit
              // and Firefox while preserving the legacy colors outside the redesign shell.
              redesign
                ? [
                    "[scrollbar-width:thin]",
                    "[&::-webkit-scrollbar-track]:tw-bg-border",
                    "[&::-webkit-scrollbar-track]:tw-rounded-full",
                    "[&::-webkit-scrollbar-thumb]:tw-bg-muted-foreground",
                    "[&::-webkit-scrollbar-thumb]:tw-rounded-full",
                    "[&::-webkit-scrollbar-thumb:hover]:tw-bg-foreground",
                  ]
                : ["[&::-webkit-scrollbar-track]:tw-bg-[#f5f5f5]", "[&::-webkit-scrollbar-thumb]:tw-bg-brand-green", "[&::-webkit-scrollbar-thumb:hover]:tw-bg-gray-500"],
            )}
          >
            {slot.times
              .filter(({ time }) => new Date(time) > minBookingTime)
              .map(({ time }) => (
                <li key={time}>
                  <AnimatedTimeSlotLink to={slotLink(time)} time={time} isActive={time === selectedTime} onClick={() => onTimeSelect(time)} redesign={redesign} />
                </li>
              ))}
          </ul>
        </>
      ) : selectedDate && slot && !hasAvailableSlots(slot) ? (
        <p className={clsx("tw-m-0 tw-p-5 tw-text-xs", redesign ? "tw-font-sans tw-text-muted-foreground" : "tw-text-[#adadad]")}>
          <FormattedMessage id="meetings.no_time_available" />
        </p>
      ) : null}
    </div>
  );
}

function MobileSlots({
  selectedTime,
  slot,
  isLoading,
  onSelectedTimeChange,
}: {
  selectedTime: DateTimeStringUTC;
  slot?: TMeetingSlot;
  isLoading: boolean;
  onSelectedTimeChange(slot: DateTimeStringUTC): void;
}) {
  const slotLink = useSlotLink();
  const redesign = useIsRedesignMeetings();
  const isEmpty = slot == undefined && !isLoading;
  const now = new Date();
  // Minimum time is 1 hour from now (for clinical expert bookings)
  const minBookingTime = new Date(now.getTime() + 60 * 60 * 1000);

  return (
    <div
      className={clsx("tw-border-t sm:tw-hidden", redesign ? "tw-border-border" : "tw-border-[#f1f1f1]", {
        "tw-flex tw-justify-center": isLoading,
      })}
    >
      {isLoading ? (
        <Loader className="tw-p-5" />
      ) : isEmpty ? (
        <p className={clsx("tw-m-0 tw-p-5 tw-text-xs", redesign ? "tw-font-sans tw-text-muted-foreground" : "tw-text-[#adadad]")}>
          <FormattedMessage id="meetings.no_time_available" />
        </p>
      ) : slot && hasAvailableSlots(slot) ? (
        <>
          <TimeZoneDisplay />

          <RadioGroup.Root value={selectedTime} onValueChange={onSelectedTimeChange} className="tw-grid tw-gap-2.5 tw-p-5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(98px, 1fr))" }}>
            {slot.times
              .filter(({ time }) => new Date(time) > minBookingTime)
              .map(({ time }) => (
                <RadioGroup.Item
                  key={time}
                  value={time}
                  data-testid="meetings-mobile-time-slot"
                  className={clsx(
                    "tw-border tw-bg-transparent tw-py-2",
                    redesign ? "tw-rounded-lg tw-border-border tw-font-sans" : "tw-border-[#f1f1f1]",
                    "data-[state=checked]:tw-border-brand-green data-[state=checked]:tw-text-brand-green",
                  )}
                >
                  {formatTime(time)}
                </RadioGroup.Item>
              ))}
          </RadioGroup.Root>

          {selectedTime ? (
            <div className={clsx("tw-border-y tw-p-5", redesign ? "tw-border-border" : "tw-border-[#f1f1f1]")}>
              <Link
                to={slotLink(selectedTime)}
                data-testid="meetings-mobile-time-slot-submit"
                className={clsx(
                  "tw-block tw-w-full tw-border-0 tw-bg-brand-green tw-py-2 tw-text-center",
                  "tw-font-semibold tw-uppercase tw-text-white active:tw-bg-brand-green-hover",
                  redesign && "tw-rounded-lg tw-font-sans",
                )}
              >
                <FormattedMessage id="meetings.book.short" />
              </Link>
            </div>
          ) : null}
        </>
      ) : (
        <p className={clsx("tw-m-0 tw-p-5 tw-text-xs", redesign ? "tw-font-sans tw-text-muted-foreground" : "tw-text-[#adadad]")}>
          <FormattedMessage id="meetings.no_time_available" />
        </p>
      )}
    </div>
  );
}

function AnimatedTimeSlotLink({ to, time, isActive, onClick, redesign }: { to: string; time: DateTimeStringUTC; isActive: boolean; onClick: () => void; redesign: boolean }) {
  // The redesign uses a filled button instead of the legacy slide-to-reveal interaction.
  if (redesign) {
    return (
      <Link
        to={to}
        data-testid="meetings-time-slot-link"
        className={clsx(
          "tw-block tw-w-full tw-rounded-lg tw-border-0 tw-py-2.5 tw-text-center tw-font-sans tw-text-sm tw-font-medium tw-transition-colors",
          isActive ? "tw-bg-brand-green tw-text-white" : "tw-bg-accent tw-text-foreground",
          "hover:tw-bg-brand-green-hover hover:tw-text-white",
          "active:tw-bg-brand-green-hover active:tw-text-white",
        )}
        onClick={onClick}
      >
        {formatTime(time)}
      </Link>
    );
  }

  return (
    <motion.div whileHover="hover" initial="initial" animate="initial">
      <Link
        to={to}
        data-testid="meetings-time-slot-link"
        className={clsx(
          "tw-relative tw-block tw-w-full tw-overflow-hidden tw-border-0",
          isActive ? "tw-bg-brand-green tw-text-white" : "tw-bg-[#f5f5f5] tw-text-brand-green",
          "tw-py-4 tw-text-xs",
          "hover:tw-bg-brand-green-hover hover:tw-text-white",
          "active:tw-bg-brand-green-hover active:tw-text-white",
        )}
        onClick={onClick}
      >
        <motion.span
          className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center"
          variants={{
            initial: { x: 0 },
            hover: { x: -70 },
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {formatTime(time)}
        </motion.span>

        <motion.span
          className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-end tw-pr-4 tw-uppercase"
          variants={{
            initial: { x: "100%" },
            hover: { x: -20 },
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {/* Match the revealed label's text colour (white on the hovered slot) instead of a fixed teal,
              so it stays legible on the recoloured-black hover bg — `currentColor` tracks the Link's text
              in every state, for all calendar usages (booking + the "Перенести встречу" popup, etc.). */}
          <Separator className="tw-mr-6 tw-h-3.5 tw-w-px tw-rounded-md tw-bg-current" />
          <FormattedMessage id="meetings.book.short" />
        </motion.span>
      </Link>
    </motion.div>
  );
}

function TimeZoneDisplay({ className }: { className?: string }) {
  return (
    <div className={clsx("tw-mt-4 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-xs", className)}>
      <i className="icon-globe tw-text-brand-green" aria-hidden="true" />
      {`${getTimeZone()} (${formatTime(new Date())})`}
    </div>
  );
}

function isPast(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

/**
 * Checks if a date has any available time slots (not past and at least 1 hour from now)
 */
function hasAvailableSlots(slot: TMeetingSlot | undefined): boolean {
  if (!slot) return false;
  const now = new Date();
  const minBookingTime = new Date(now.getTime() + 60 * 60 * 1000);
  return slot.times.some(({ time }) => new Date(time) > minBookingTime);
}

function findNearestSlotDate(slots: TMeetingSlot[]): Date | undefined {
  const now = new Date();
  // Minimum time is 1 hour from now (for clinical expert bookings)
  const minBookingTime = new Date(now.getTime() + 60 * 60 * 1000);

  for (const slot of slots) {
    if (slot.times.some(({ time }) => new Date(time) > minBookingTime)) {
      return new Date(slot.date);
    }
  }
}
